// src/layouts/MainLayout.jsx
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Url from "app/shared/constants/Url";
import axios from "axios";
import Header from "./components/header";
import LeftPanel from "./components/leftPanel";
import Footer from "./components/footer";
import RightPanelComponent from "./components/rightPanel";
import ElipseShape from "../../assets/design/elipse.png";

const DEFAULT_COMPANY_NAME_FONT_STYLE = 'normal';
const DEFAULT_COMPANY_NAME_FONT_FAMILY = 'Zoho Puvi';
const DEFAULT_TAG_LINE_FONT_STYLE = 'normal';
const DEFAULT_TAG_LINE_FONT_FAMILY = 'Zoho Puvi';

// Styled Components for MainLayout
const MainLayoutContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  background-color: ${({ $backgroundThemeColor }) => $backgroundThemeColor};
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex: 1;
  padding: 0;
  width: 100%;
  align-items: stretch;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    background: ${({ $themeColor }) => $themeColor};

    /* Top Right Position */
    top: 0;
    right: 0;

    /* Shape Size */
    width: 33%;
    height: 70%;

    /* Curved Shape */
    clip-path: ellipse(85% 100% at 100% 0%);

    z-index: 0;
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 1200px) {
    flex-direction: column;
    padding: 0;

    &::before {
      width: 60%;
      height: 40%;
    }
  }
`;

const Preloader = ({ themeColor }) => {
  return (
    <div
      className="demo-only "
      style={{
        height: "100vh",
        backgroundColor: themeColor,
        position: "relative",
      }}
    >
      <div
        className="slds-spinner_container"
        style={{ backgroundColor: themeColor }}
      >
        <div
          role="status"
          className="slds-spinner slds-spinner_large slds-spinner_inverse"
        >
          <span className="slds-assistive-text">Loading</span>
          <div className="slds-spinner__dot-a"></div>
          <div className="slds-spinner__dot-b"></div>
        </div>
      </div>
    </div>
  );
};

const OnBoardingLayout = ({
  children,
  formTitle = "Form Rest",
  content = {},
}) => {
  // Check localStorage for cached login page settings to enable instant load
  const getCachedSettings = () => {
    try {
      const cached = localStorage.getItem("login_page_settings");
      return cached ? JSON.parse(cached) : null;
    } catch (e) {
      return null;
    }
  };

  const initialSettings = getCachedSettings();

  const [dataLoading, setDataLoading] = useState(!initialSettings);
  const [themeColor, setThemeColor] = useState(initialSettings?.color || "#f56620");
  const [leftPanelLogo, setLeftPanelLogo] = useState(initialSettings?.sideLogo || null);
  const [rightPanelLogoSize, setRightPanelLogoSize] = useState(initialSettings?.logoSize || "300");
  const [carouselBannerImages, setCarouselBannerImages] = useState(initialSettings?.carousels || []);
  const [companyNameFontSize, setCompanyNameFontSize] = useState(initialSettings?.companyNameSize || "25.6");
  const [navMenuEnabled, setNavMenuEnabled] = useState(initialSettings?.navMenuEnabled ?? 1);
  const [socialMediaEnabled, setSocialMediaEnabled] = useState(initialSettings?.socialMediaEnabled ?? 1);
  const [carouselAutoscrollEnabled, setCarouselAutoscrollEnabled] = useState(initialSettings?.carouselEnabled ?? 1);
  const [companyTaglineFontSize, setCompanyTaglineFontSize] = useState(initialSettings?.taglineSize || "12");
  const [isCompanyNameBold, setIsCompanyNameBold] = useState(initialSettings?.companyNameBold || 0);
  const [isCompanyTagLineBold, setIsCompanyTagLineBold] = useState(initialSettings?.taglineBold || 0);
  const [backgroundThemeColor, setBackgroundThemeColor] = useState(initialSettings?.backgroundColor || "#ffffff");
  const [companyNameFontFamily, setCompanyNameFontFamily] = useState(initialSettings?.companyNameFontFamily || DEFAULT_COMPANY_NAME_FONT_FAMILY);
  const [companyNameFontStyle, setCompanyNameFontStyle] = useState(initialSettings?.companyNameFontStyle || DEFAULT_COMPANY_NAME_FONT_STYLE);
  const [companyTagLineFontFamily, setCompanyTagLineFontFamily] = useState(initialSettings?.taglineFontFamily || DEFAULT_TAG_LINE_FONT_FAMILY);
  const [companyTagLineFontStyle, setCompanyTagLineFontStyle] = useState(initialSettings?.taglineFontStyle || DEFAULT_TAG_LINE_FONT_STYLE);

  useEffect(() => {
    fetchLoginPageSettings();
  }, []);

  const applySettingsData = (resp) => {
    setThemeColor(resp.color);
    setBackgroundThemeColor(resp.backgroundColor || "#ffffff");
    setLeftPanelLogo(resp.sideLogo);
    setRightPanelLogoSize(resp.logoSize);

    let carouselImages = resp.carousels || [];
    const sortedCarouselImages = [...carouselImages].sort(
      (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0),
    );

    setCarouselBannerImages(sortedCarouselImages);
    setCompanyNameFontSize(resp.companyNameSize);
    setNavMenuEnabled(resp.navMenuEnabled);
    setSocialMediaEnabled(resp.socialMediaEnabled);
    setCarouselAutoscrollEnabled(resp.carouselEnabled);
    setCompanyTaglineFontSize(resp.taglineSize);
    setIsCompanyNameBold(resp.companyNameBold || 0);
    setIsCompanyTagLineBold(resp.taglineBold || 0);
    setCompanyNameFontFamily(resp.companyNameFontFamily);
    setCompanyNameFontStyle(resp.companyNameFontStyle);
    setCompanyTagLineFontFamily(resp.taglineFontFamily);
    setCompanyTagLineFontStyle(resp.taglineFontStyle);
  };

  const fetchLoginPageSettings = async () => {
    let userId = localStorage.getItem("userId");
    let token = localStorage.getItem("token");

    const requestData = {
      status: 1,
      link: Url.clientURL,
      auth_key: token,
      user_id: userId,
    };

    try {
      const response = await axios.get(Url.loginPageSettings, {
        params: requestData,
      });

      if (response.data && response.data.status === 200) {
        let resp = response.data.data;
        applySettingsData(resp);
        try {
          localStorage.setItem("login_page_settings", JSON.stringify(resp));
        } catch (e) {}
      }
    } catch (error) {
      console.error("Failed to fetch login page settings", error);
    } finally {
      setDataLoading(false);
    }
  };

  return (
    <MainLayoutContainer $backgroundThemeColor={backgroundThemeColor}>
      {dataLoading ? (
        <Preloader themeColor={themeColor} />
      ) : (
        <>
          <Header themeColor={themeColor} navMenuEnabled={navMenuEnabled} />
          <ContentWrapper $themeColor={themeColor}>
            <LeftPanel
              themeColor={themeColor}
              leftPanelLogo={leftPanelLogo}
              carouselBannerImages={carouselBannerImages}
              carouselAutoscrollEnabled={carouselAutoscrollEnabled}
            />
            <RightPanelComponent
              formTitle={formTitle}
              content={content}
              themeColor={themeColor}
              companyNameFontSize={companyNameFontSize}
              rightPanelLogoSize={rightPanelLogoSize}
              companyTaglineFontSize={companyTaglineFontSize}
              isCompanyNameBold={isCompanyNameBold}
              isCompanyTagLineBold={isCompanyTagLineBold}
              companyNameFontFamily={companyNameFontFamily}
              companyNameFontStyle={companyNameFontStyle}
              companyTagLineFontFamily={companyTagLineFontFamily}
              companyTagLineFontStyle={companyTagLineFontStyle}
            >
              {children}
            </RightPanelComponent>
          </ContentWrapper>
          <Footer
            themeColor={themeColor}
            socialMediaEnabled={socialMediaEnabled}
          />
        </>
      )}
    </MainLayoutContainer>
  );
};

export default OnBoardingLayout;
